import { formatDate } from '@angular/common';
import { parseISO } from 'date-fns';
import { Image } from './image';
import { Status } from './status';
import {
  faSignature,
  faTruck,
  faTruckFast,
  faTruckRampBox,
} from '@fortawesome/free-solid-svg-icons';
import { ProgressBarCustom } from './progressBarColor';
import { Log } from '../services/shared/log';

/**
 * Helpers — namespace unificato.
 *
 * Storicamente esistevano due file paralleli con lo stesso nome di identifier:
 *  - `helpers.ts`  → namespace Helpers con sole costanti Permission
 *  - `helpters.ts` → class Helpers con icone, formatter e progress bar
 *
 * Erano due API completamente disgiunte sotto lo stesso nome, importate in
 * modo incoerente nei vari moduli. Da T3 della Fase 0 sono unificate qui.
 * `helpters.ts` è stato rimosso.
 */
export namespace Helpers {

  // -------------------------------------------------------------------------
  // Permission codes (ex namespace originale in helpers.ts)
  // -------------------------------------------------------------------------

  export type Permission = 0 | 1 | 2 | 3 | 4 | 'AGENT' | 'NONE';
  export const PermisionSuperAdmin: Permission = 0;
  export const PermisionAdmin: Permission = 1;
  export const PermisionUser: Permission = 2;
  export const PermisionScreen: Permission = 3;
  export const PermisionAgent: Permission = 'AGENT';
  export const PermisionManufacturer: Permission = 4;
  export const PermisionNone: Permission = 'NONE';

  // -------------------------------------------------------------------------
  // Costanti varie (ex class statics in helpters.ts)
  // -------------------------------------------------------------------------

  export const adminMessageId: number = -99;
  export const base64Length: number = 19;
  export const base64PNGLength: number = 3;

  // -------------------------------------------------------------------------
  // Date / time formatting
  // -------------------------------------------------------------------------

  export function getDate(datefrom: string | null | undefined): string {
    if (!datefrom || isNaN(Date.parse(datefrom))) {
      return '-';
    }
    try {
      return formatDate(parseISO(datefrom), 'dd/MM/yy', 'it-IT');
    } catch (error) {
      Log.error('Errore nel parsing della data:', datefrom, error);
      return '-';
    }
  }

  export function getTime(timeFrom: string): string {
    return formatDate(parseISO(timeFrom), 'HH:mm', 'it-IT');
  }

  // -------------------------------------------------------------------------
  // Validators
  // -------------------------------------------------------------------------

  export function checkCBoxForNull(
    ts_box_height: number,
    ts_box_length: number,
    ts_box_depth: number,
    ts_box_weight: number,
  ): boolean {
    if (!ts_box_height) return true;
    if (!ts_box_length) return true;
    if (!ts_box_depth) return true;
    if (!ts_box_weight) return true;
    return false;
  }

  // -------------------------------------------------------------------------
  // Icons (Image.IImage)
  // -------------------------------------------------------------------------

  export const iconFree: Image.IImage = {
    icon: 'checkmark-circle',
    label: 'Libero',
    status: Status.StatusesUser.assigned,
    class: 'color-green',
    bgClass: 'background-green',
  };

  export const iconUnavailable: Image.IImage = {
    icon: 'ban',
    label: 'Prenotato',
    status: -1,
    class: 'color-red',
    bgClass: 'background-unavailable-item',
  };

  export const iconTaskWorking: Image.IImage = {
    icon: 'timer',
    label: 'In lavorazione',
    status:
      Status.StatusesUser.step1 |
      Status.StatusesUser.step2 |
      Status.StatusesUser.step3 |
      Status.StatusesUser.step4,
    class: 'color-orange',
    bgClass: 'background-orange',
  };

  export const iconTaskSuspended: Image.IImage = {
    icon: 'pause-circle',
    label: 'Sospeso',
    status: Status.StatusesUser.suspended,
    class: 'color-red',
    bgClass: 'background-red',
  };

  export const iconTaskBlocked: Image.IImage = {
    icon: 'lock',
    label: 'Bloccato',
    status: Status.StatusesUser.blocked,
    class: 'color-white',
    bgClass: 'background-black',
  };

  export const iconTaskUnBlocked: Image.IImage = {
    icon: 'lock-open',
    label: 'Sbloccato',
    status: Status.StatusesUser.unblocked,
    class: 'color-black',
    bgClass: 'background-orange',
  };

  export const iconTaskComplete: Image.IImage = {
    icon: 'checkmark-circle',
    label: 'Completato',
    status: Status.StatusesUser.step5,
    class: 'color-inactive',
    bgClass: 'white-transparency',
  };

  export const iconSignature: Image.IImage = {
    faElement: faSignature,
    icon: null, //'pencil-outline',
    label: 'Firma richesta',
    status: Status.StatusesUser.pronto,
    class: 'color-red',
    bgClass: '',
  };

  export const iconTaskNew: Image.IImage = {
    icon: 'play-circle',
    label: 'Inizia',
    status: Status.StatusesUser.intiated,
    class: 'color-green',
    bgClass: 'background-green',
  };

  export const iconMail: Image.IImage = {
    icon: '',
    label: 'Letto',
    status: 1,
    class: 'color-green',
    bgClass: '',
  };

  export const iconMailOpen: Image.IImage = {
    icon: 'alert-circle',
    label: 'Da Leggere',
    status: -1,
    class: 'color-red',
    bgClass: '',
  };

  export const iconShowProducts: Image.IImage = {
    icon: 'albums',
    label: 'Pulsante mostra prodotti',
    status: -1,
    class: 'color-light-blue',
    bgClass: '',
  };

  export const iconCarrierDelivered: Image.IImage = {
    faElement: faTruck,
    icon: null,
    label: 'Consegnato',
    status: 6 | Status.StatusesUser.atClient,
    class: 'color-green',
    bgClass: '',
  };

  export const iconInfoReceived: Image.IImage = {
    faElement: faTruckRampBox,
    icon: null,
    label: 'Informazioni ricevute',
    status: 1,
    class: 'color-light-blue',
    bgClass: '',
  };

  export const iconInTransit: Image.IImage = {
    faElement: faTruck,
    icon: null,
    label: 'In transito',
    status: 2 | Status.StatusesUser.shipped,
    class: 'color-light-blue',
    bgClass: '',
  };

  export const iconOutForDelevery: Image.IImage = {
    faElement: faTruckFast,
    icon: null,
    label: 'In consegna',
    status: 6 | Status.StatusesUser.inTranzit,
    class: 'color-light-blue',
    bgClass: '',
  };

  export const iconMissedDelivery: Image.IImage = {
    faElement: faTruck,
    icon: null,
    label: 'Consegna persa',
    status: 6,
    class: 'color-red',
    bgClass: '',
  };

  export const iconException: Image.IImage = {
    icon: 'close-circle',
    label: 'Eccezione',
    status: 6,
    class: 'color-red',
    bgClass: '',
  };

  export const iconAttention: Image.IImage = {
    icon: 'alert',
    label: 'Ordini parziali',
    status: -1,
    class: 'color-white',
    bgClass: 'background-white',
  };

  // -------------------------------------------------------------------------
  // Progress bar presets
  // -------------------------------------------------------------------------

  export const progresBar0p: ProgressBarCustom.IProgresBar = {
    perc: 0,
    cssClass: 'k-selected color-red width-0',
  };

  export const progresBar20p: ProgressBarCustom.IProgresBar = {
    perc: 0.2,
    cssClass: 'k-selected color-red width-20',
  };

  export const progresBar40p: ProgressBarCustom.IProgresBar = {
    perc: 0.7,
    cssClass: 'k-selected color-light-orange width-70',
  };

  export const progresBar60p: ProgressBarCustom.IProgresBar = {
    perc: 0.6,
    cssClass: 'k-selected color-yellow width-60',
  };

  export const progresBar80p: ProgressBarCustom.IProgresBar = {
    perc: 0.8,
    cssClass: 'k-selected color-light-yellow width-80',
  };

  export const progresBar100p: ProgressBarCustom.IProgresBar = {
    perc: 1,
    cssClass: ' k-selected color-green width-100',
  };

}
